import { useRef } from "react";
import { Link } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Fade, Grid, Grow, TextField, Typography } from "@mui/material";
import {
    ArrowBack as ArrowBackIcon,
    Security as SecurityIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { api } from "../../services/api";
import { FormState, getErrorIcon, getErrorSeverity } from "./PasswordFunctions";

type RenderVerificationProps = {
    formState: FormState;
    setFormState: React.Dispatch<React.SetStateAction<FormState>>
}

const RenderVerificationStep = ({ formState, setFormState }: RenderVerificationProps) => {
    const codeInputRefs = useRef<(HTMLInputElement | null)[]>([]);

    const handleCodeChange = (index: number, value: string) => {
        if (value.length > 1) return;

        const newCode = formState.verificationCode.split('');
        newCode[index] = value;
        const updatedCode = newCode.join('');

        setFormState(prev => ({
            ...prev,
            verificationCode: updatedCode,
            error: null,
            errorType: null,
        }));

        // Auto-focus next input
        if (value && index < 5) {
            codeInputRefs.current[index + 1]?.focus();
        }
    };

    const handleCodeKeyDown = (index: number, event: React.KeyboardEvent) => {
        if (event.key === 'Backspace' && !formState.verificationCode[index] && index > 0) {
            codeInputRefs.current[index - 1]?.focus();
        }
    };

    const handleVerifyCode = async () => {
        if (formState.verificationCode.length !== 6) {
            setFormState(prev => ({
                ...prev,
                error: 'Por favor, insira o código de 6 dígitos completo',
                errorType: 'validation',
            }));
            return;
        }

        setFormState(prev => ({
            ...prev,
            isLoading: true,
            error: null,
            errorType: null,
        }));

        try {
            const response = await api.get(`/verifiquecode/${formState.verificationCode}`);
            if (response.data.success) {
                setFormState(prev => ({
                    ...prev,
                    id_usuario: response.data.id_usuario,
                    isLoading: false,
                    isCodeVerified: true,
                }));
            } else {
                setFormState(prev => ({
                    ...prev,
                    isLoading: false,
                    error: 'Código de verificação inválido. Verifique o código enviado por email.',
                    errorType: 'invalid-code',
                }));
            }

        } catch {
            setFormState(prev => ({
                ...prev,
                isLoading: false,
                error: 'Erro interno do servidor. Tente novamente mais tarde.',
                errorType: 'server',
            }));
        }
    };

    const handleResendCode = async () => {
        if (formState.resendCooldown > 0) return;

        setFormState(prev => ({
            ...prev,
            isLoading: true,
            error: null,
            errorType: null,
            verificationCode: '',
        }));

        try {
            const response = await api.post(`/recuperarsenha`, { email_usuario: formState.email });
            if (response.data.success) {
                setFormState(prev => ({
                    ...prev,
                    isLoading: false,
                    resendCooldown: 60,
                }));

                // Clear code inputs
                codeInputRefs.current.forEach(ref => {
                    if (ref) ref.value = '';
                });
            }
        } catch {
            setFormState(prev => ({
                ...prev,
                isLoading: false,
                error: 'Erro ao reenviar código. Tente novamente.',
                errorType: 'server',
            }));
        }
    };

    return (
        <Grow in timeout={800}>
            <Box>
                <Alert
                    severity="info"
                    sx={{
                        mb: 3,
                        borderRadius: 2,
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        borderColor: 'primary.main',
                    }}
                >
                    <Typography variant="body2">
                        Código enviado para <strong>{formState.email}</strong>
                    </Typography>
                </Alert>

                <Typography variant="h6" gutterBottom textAlign="center" sx={{ mb: 3 }}>
                    Digite o código de verificação
                </Typography>

                <Grid container spacing={1} justifyContent="center" sx={{ mb: 3 }}>
                    {[0, 1, 2, 3, 4, 5].map((index) => (
                        <Grid item key={index}>
                            <TextField
                                inputRef={(el) => (codeInputRefs.current[index] = el)}
                                variant="outlined"
                                inputProps={{
                                    maxLength: 1,
                                    style: {
                                        textAlign: 'center',
                                        fontSize: '1.5rem',
                                        fontWeight: 'bold',
                                    },
                                }}
                                onChange={(e) => handleCodeChange(index, e.target.value)}
                                onKeyDown={(e) => handleCodeKeyDown(index, e)}
                                sx={{
                                    width: 56,
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        },
                                        '&.Mui-focused': {
                                            transform: 'translateY(-1px)',
                                            boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                    ))}
                </Grid>

                {formState.error && (
                    <Fade in>
                        <Alert
                            severity={getErrorSeverity(formState)}
                            icon={getErrorIcon(formState)}
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                '& .MuiAlert-icon': {
                                    fontSize: 20,
                                },
                            }}
                        >
                            <Typography variant="body2" component="div">
                                {formState.error}
                            </Typography>
                        </Alert>
                    </Fade>
                )}

                <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    onClick={handleVerifyCode}
                    disabled={formState.verificationCode.length !== 6 || formState.isLoading}
                    startIcon={
                        formState.isLoading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            <SecurityIcon />
                        )
                    }
                    sx={{
                        py: 1.5,
                        mb: 2,
                        borderRadius: 2,
                        background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
                        boxShadow: '0 4px 16px rgba(76, 175, 80, 0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                            background: 'linear-gradient(45deg, #45a049 30%, #3d8b40 90%)',
                        },
                        '&:disabled': {
                            transform: 'none',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        },
                    }}
                >
                    {formState.isLoading ? 'Verificando...' : 'Verificar Código'}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button
                        variant="text"
                        onClick={handleResendCode}
                        disabled={formState.resendCooldown > 0 || formState.isLoading}
                        startIcon={<RefreshIcon />}
                        sx={{
                            borderRadius: 2,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(33, 150, 243, 0.04)',
                            },
                        }}
                    >
                        {formState.resendCooldown > 0
                            ? `Reenviar em ${formState.resendCooldown}s`
                            : 'Reenviar Código'
                        }
                    </Button>

                    <Link to="/login">
                        <Button
                            variant="text"
                            startIcon={<ArrowBackIcon />}
                            sx={{
                                borderRadius: 2,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: 'rgba(33, 150, 243, 0.04)',
                                    transform: 'translateX(-4px)',
                                },
                            }}
                        >
                            Voltar ao Login
                        </Button>
                    </Link>
                </Box>
            </Box>
        </Grow>
    );
}

export default RenderVerificationStep;